import type { FilteredListingControlsSnapshot } from './useFilteredListingControls';

/** Shared default page size for all FilteredListing screens. Override per data source only when justified. */
export const DEFAULT_LISTING_PAGE_SIZE = 20;

export type ListingQuerySnapshot<TFilters> =
  FilteredListingControlsSnapshot<TFilters>;

export type ListingPaginationContext = {
  cursor?: string | null;
  page?: number;
  pageSize: number;
};

export type ListingFetchResult<TRow, TFilterCounts = unknown> = {
  rows: TRow[];
  totalCount: number;
  nextCursor?: string | null;
  previousCursor?: string | null;
  filterCounts?: TFilterCounts;
};

type ListingPaginationMode = 'cursor' | 'offset';

export type ListingDataSource<
  TRow,
  TFilters,
  TContext = void,
  TFilterCounts = unknown,
> = {
  /** Stable id for React Query cache keys and table preferences. */
  listingId: string;
  /** Defaults to `'cursor'`. Use `'offset'` when the API uses page numbers and all pages are directly reachable. */
  paginationMode?: ListingPaginationMode;
  /** Defaults to {@link DEFAULT_LISTING_PAGE_SIZE} when omitted. */
  pageSize?: number;
  mapToQueryParams: (
    snapshot: ListingQuerySnapshot<TFilters>,
    context: TContext,
    pagination: ListingPaginationContext,
  ) => URLSearchParams | string;
  fetch: (
    query: URLSearchParams | string,
    snapshot: ListingQuerySnapshot<TFilters>,
    context: TContext,
  ) => Promise<ListingFetchResult<TRow, TFilterCounts>>;
};

export function resolveListingPageSize(dataSource: {
  pageSize?: number;
}): number {
  return dataSource.pageSize ?? DEFAULT_LISTING_PAGE_SIZE;
}
